import Chart from 'chart.js/auto';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import jsQR from 'jsqr';

const chartInstances = new Map();

const moneyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('es-AR');

function formatChartValue(value, format = 'number') {
    if (format === 'money') {
        return moneyFormatter.format(value);
    }

    if (format === 'percent') {
        return `${numberFormatter.format(value)}%`;
    }

    return numberFormatter.format(value);
}

function readChartConfig(wrapper) {
    const source = wrapper.querySelector('[data-ticket-chart-config]');

    if (! source?.textContent?.trim()) {
        return null;
    }

    return JSON.parse(source.textContent);
}

function chartOptions(config) {
    const valueFormat = config.valueFormat || 'number';
    const axisColor = 'rgba(113, 113, 122, .72)';
    const gridColor = 'rgba(113, 113, 122, .14)';

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {
                display: config.legend !== false,
                position: config.legendPosition || 'bottom',
                labels: {
                    usePointStyle: true,
                    boxHeight: 8,
                    boxWidth: 8,
                    color: axisColor,
                    font: {
                        family: 'Inter, ui-sans-serif, system-ui, sans-serif',
                        size: 12,
                    },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(24, 24, 27, .95)',
                borderColor: 'rgba(255, 255, 255, .12)',
                borderWidth: 1,
                callbacks: {
                    label(context) {
                        const label = context.dataset.label ? `${context.dataset.label}: ` : '';
                        const rawValue = Array.isArray(context.raw) ? context.raw[0] : context.raw;

                        return `${label}${formatChartValue(Number(rawValue || 0), valueFormat)}`;
                    },
                },
            },
        },
    };

    if (config.type === 'doughnut') {
        return {
            ...options,
            cutout: '68%',
            interaction: {
                intersect: true,
                mode: 'nearest',
            },
        };
    }

    return {
        ...options,
        indexAxis: config.indexAxis || 'x',
        scales: {
            x: {
                stacked: config.stacked === true,
                grid: {
                    display: config.indexAxis === 'y',
                    color: gridColor,
                },
                ticks: {
                    color: axisColor,
                    callback(value) {
                        if (config.indexAxis === 'y') {
                            return formatChartValue(Number(value), valueFormat);
                        }

                        return this.getLabelForValue(value);
                    },
                },
            },
            y: {
                stacked: config.stacked === true,
                beginAtZero: true,
                grid: {
                    color: gridColor,
                },
                ticks: {
                    color: axisColor,
                    callback(value) {
                        if (config.indexAxis === 'y') {
                            return this.getLabelForValue(value);
                        }

                        return formatChartValue(Number(value), valueFormat);
                    },
                },
            },
        },
    };
}

function normalizeConfig(config) {
    return {
        type: config.type,
        data: config.data,
        options: chartOptions(config),
    };
}

function renderChart(wrapper, nextConfig = null) {
    const canvas = wrapper.querySelector('canvas');
    const chartId = wrapper.dataset.chartId;
    const config = nextConfig || readChartConfig(wrapper);

    if (! canvas || ! chartId || ! config) {
        return;
    }

    const current = chartInstances.get(chartId);

    if (current) {
        current.data = config.data;
        current.options = chartOptions(config);
        current.update();
        return;
    }

    chartInstances.set(chartId, new Chart(canvas, normalizeConfig(config)));
}

function initTicketCharts(root = document) {
    root.querySelectorAll('[data-ticket-chart]').forEach((wrapper) => renderChart(wrapper));
}

function resizeTicketCharts(root = document) {
    root.querySelectorAll('[data-ticket-chart]').forEach((wrapper) => {
        const chart = chartInstances.get(wrapper.dataset.chartId);

        chart?.resize();
    });
}

const stadiumEditors = new WeakSet();
const stadiumGeometryCache = new Map();
const stadiumActivePanelCache = new Map();
const publicCheckouts = new WeakSet();
const publicCheckoutSendTimers = new WeakMap();
const eventLocationMaps = new WeakMap();
const qrScanners = new WeakSet();
const qrScannerStates = new WeakMap();
const STADIUM_GRID_STEP = 5;
const STADIUM_MIN_BLOCK_SIZE = 10;
const STADIUM_STAGE_CACHE_KEY = '__stage__';
const QR_SCANNER_CLEAR_REPEAT_MS = 900;
const QR_SCANNER_RESULT_HOLD_MS = 2000;

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function readBlockGeometry(block) {
    return {
        x: Number(block.dataset.x || 0),
        y: Number(block.dataset.y || 0),
        w: Number(block.dataset.w || 20),
        h: Number(block.dataset.h || 20),
    };
}

function snapStadiumGridValue(value) {
    return Math.round(Number(value || 0) / STADIUM_GRID_STEP) * STADIUM_GRID_STEP;
}

function snapStadiumGeometry(geometry) {
    const w = clamp(snapStadiumGridValue(geometry.w), STADIUM_MIN_BLOCK_SIZE, 100);
    const h = clamp(snapStadiumGridValue(geometry.h), STADIUM_MIN_BLOCK_SIZE, 100);

    return {
        x: clamp(snapStadiumGridValue(geometry.x), 0, 100 - w),
        y: clamp(snapStadiumGridValue(geometry.y), 0, 100 - h),
        w,
        h,
    };
}

function applyBlockGeometry(block, geometry) {
    block.dataset.x = String(geometry.x);
    block.dataset.y = String(geometry.y);
    block.dataset.w = String(geometry.w);
    block.dataset.h = String(geometry.h);
    block.style.left = `${geometry.x}%`;
    block.style.top = `${geometry.y}%`;
    block.style.width = `${geometry.w}%`;
    block.style.height = `${geometry.h}%`;
}

function stadiumEditorKey(editor) {
    return editor.dataset.stadiumEditorKey || window.location.pathname;
}

function stadiumBlockKey(block) {
    return block.dataset.blockKey || block.dataset.index;
}

function rememberStadiumBlock(editor, block) {
    const editorKey = stadiumEditorKey(editor);
    const blockKey = stadiumBlockKey(block);

    if (! editorKey || ! blockKey) {
        return;
    }

    if (! stadiumGeometryCache.has(editorKey)) {
        stadiumGeometryCache.set(editorKey, new Map());
    }

    stadiumGeometryCache.get(editorKey).set(blockKey, readBlockGeometry(block));
}

function rememberStadiumStage(editor) {
    const stageObject = editor.querySelector('[data-stadium-stage-object]');
    const editorKey = stadiumEditorKey(editor);

    if (! stageObject || ! editorKey) {
        return;
    }

    if (! stadiumGeometryCache.has(editorKey)) {
        stadiumGeometryCache.set(editorKey, new Map());
    }

    stadiumGeometryCache.get(editorKey).set(STADIUM_STAGE_CACHE_KEY, readBlockGeometry(stageObject));
}

function rememberStadiumEditor(editor) {
    editor.querySelectorAll('[data-stadium-block]').forEach((block) => rememberStadiumBlock(editor, block));
    rememberStadiumStage(editor);
}

function rememberAllStadiumEditors() {
    document.querySelectorAll('[data-stadium-editor]').forEach((editor) => rememberStadiumEditor(editor));
}

function collectStadiumGeometry(editor) {
    return Array.from(editor.querySelectorAll('[data-stadium-block]')).map((block) => ({
        index: Number(block.dataset.index || 0),
        ...readBlockGeometry(block),
    }));
}

function collectStadiumStageGeometry(editor) {
    const stageObject = editor.querySelector('[data-stadium-stage-object]');

    return stageObject ? readBlockGeometry(stageObject) : { x: 35, y: 5, w: 30, h: 10 };
}

function writeStadiumGeometryPayload(editor, geometry = null) {
    const input = editor.closest('[data-ticket-event-form]')?.querySelector('[data-stadium-geometry-payload]');
    const payload = JSON.stringify(geometry || collectStadiumGeometry(editor));

    if (! (input instanceof HTMLInputElement)) {
        return payload;
    }

    input.value = payload;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    return payload;
}

function writeStadiumStagePayload(editor, geometry = null) {
    const input = editor.closest('[data-ticket-event-form]')?.querySelector('[data-stadium-stage-payload]');
    const payload = JSON.stringify(geometry || collectStadiumStageGeometry(editor));

    if (! (input instanceof HTMLInputElement)) {
        return payload;
    }

    input.value = payload;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    return payload;
}

function writeActiveStadiumPanel(editor, index) {
    const normalizedIndex = String(index ?? 0);
    const input = editor.closest('[data-ticket-event-form]')?.querySelector('[data-stadium-active-index]');

    if (input instanceof HTMLInputElement) {
        input.value = normalizedIndex;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const component = livewireComponentFor(editor);

    if (component?.set) {
        component.set('activeStadiumZoneIndex', Number(normalizedIndex), false);
    }
}

function restoreStadiumEditor(editor) {
    const cache = stadiumGeometryCache.get(stadiumEditorKey(editor));

    if (! cache) {
        return;
    }

    editor.querySelectorAll('[data-stadium-block]').forEach((block) => {
        const geometry = cache.get(stadiumBlockKey(block));

        if (! geometry) {
            return;
        }

        applyBlockGeometry(block, geometry);
        syncStadiumInputs(editor, block);
    });

    const stageObject = editor.querySelector('[data-stadium-stage-object]');
    const stageGeometry = cache.get(STADIUM_STAGE_CACHE_KEY);

    if (stageObject && stageGeometry) {
        applyBlockGeometry(stageObject, stageGeometry);
        syncStadiumStageInputs(editor, stageObject);
    }
}

function activePanelKey(editor) {
    return stadiumEditorKey(editor);
}

function activateStadiumPanel(editor, index, scroll = false) {
    const normalizedIndex = String(index);
    stadiumActivePanelCache.set(activePanelKey(editor), normalizedIndex);
    writeActiveStadiumPanel(editor, normalizedIndex);

    editor.querySelectorAll('[data-stadium-panel]').forEach((panel) => {
        const isActive = panel.dataset.index === normalizedIndex;
        const body = panel.querySelector('[data-stadium-panel-body]');
        const trigger = panel.querySelector('[data-stadium-accordion-trigger]');

        panel.dataset.active = isActive ? 'true' : 'false';
        panel.classList.toggle('ring-2', isActive);
        panel.classList.toggle('ring-lime-400', isActive);
        body?.classList.toggle('hidden', ! isActive);
        trigger?.setAttribute('aria-expanded', isActive ? 'true' : 'false');

        if (isActive && scroll) {
            panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    });

    editor.querySelectorAll('[data-stadium-block]').forEach((block) => {
        const isActive = block.dataset.index === normalizedIndex;

        block.classList.toggle('z-20', isActive);
        block.classList.toggle('ring-2', isActive);
        block.classList.toggle('ring-lime-400', isActive);
    });
}

function restoreActiveStadiumPanel(editor) {
    const cachedIndex = stadiumActivePanelCache.get(activePanelKey(editor));
    const inputIndex = editor.closest('[data-ticket-event-form]')?.querySelector('[data-stadium-active-index]')?.value;
    const serverActivePanel = editor.querySelector('[data-stadium-panel][data-active="true"]');
    const firstPanel = editor.querySelector('[data-stadium-panel]');
    const index = cachedIndex ?? inputIndex ?? serverActivePanel?.dataset.index ?? firstPanel?.dataset.index;

    if (index !== undefined) {
        activateStadiumPanel(editor, index);
    }
}

function updateStadiumInput(editor, index, key, value) {
    const input = editor.querySelector(`[data-stadium-input="${index}:${key}"]`);

    if (! input) {
        return;
    }

    const nextValue = String(Math.round(value));
    input.value = nextValue;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
}

function livewireComponentFor(element) {
    const root = element.closest('[wire\\:id]');
    const id = root?.getAttribute('wire:id');

    if (! id || ! window.Livewire?.find) {
        return null;
    }

    return window.Livewire.find(id);
}

async function syncStadiumGeometryToLivewire(editor) {
    rememberStadiumEditor(editor);
    editor.querySelectorAll('[data-stadium-block]').forEach((block) => syncStadiumInputs(editor, block));
    const stageObject = editor.querySelector('[data-stadium-stage-object]');
    if (stageObject) {
        syncStadiumStageInputs(editor, stageObject);
    }
    const geometry = collectStadiumGeometry(editor);
    const stageGeometry = collectStadiumStageGeometry(editor);
    writeStadiumGeometryPayload(editor, geometry);
    writeStadiumStagePayload(editor, stageGeometry);

    const component = livewireComponentFor(editor);

    if (! component?.call) {
        return;
    }

    await component.call('syncStadiumStageGeometry', stageGeometry);
    await component.call('syncStadiumGeometry', geometry);
}

function syncStadiumInputs(editor, block) {
    const index = block.dataset.index;
    const geometry = readBlockGeometry(block);

    updateStadiumInput(editor, index, 'x', geometry.x);
    updateStadiumInput(editor, index, 'y', geometry.y);
    updateStadiumInput(editor, index, 'w', geometry.w);
    updateStadiumInput(editor, index, 'h', geometry.h);
}

function syncStadiumStageInputs(editor, stageObject) {
    const geometry = readBlockGeometry(stageObject);

    ['x', 'y', 'w', 'h'].forEach((key) => {
        const input = editor.closest('[data-ticket-event-form]')?.querySelector(`[data-stadium-stage-input="${key}"]`);

        if (! input) {
            return;
        }

        input.value = String(Math.round(geometry[key]));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

function prepareStadiumFormPayload(form) {
    const editor = form.querySelector('[data-stadium-editor]');

    if (! editor) {
        return {
            activeIndex: 0,
            payload: '[]',
        };
    }

    const activeIndex = stadiumActivePanelCache.get(activePanelKey(editor))
        ?? form.querySelector('[data-stadium-active-index]')?.value
        ?? editor.querySelector('[data-stadium-panel][data-active="true"]')?.dataset.index
        ?? editor.querySelector('[data-stadium-panel]')?.dataset.index
        ?? 0;

    writeActiveStadiumPanel(editor, activeIndex);
    rememberStadiumEditor(editor);
    editor.querySelectorAll('[data-stadium-block]').forEach((block) => syncStadiumInputs(editor, block));
    const stageObject = editor.querySelector('[data-stadium-stage-object]');
    if (stageObject) {
        syncStadiumStageInputs(editor, stageObject);
    }

    return {
        activeIndex: Number(activeIndex),
        payload: writeStadiumGeometryPayload(editor),
        stagePayload: writeStadiumStagePayload(editor),
    };
}

function flushTicketEventFormInputs(form) {
    form.querySelectorAll('input, textarea, select').forEach((field) => {
        if (! (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
            return;
        }

        if (field.disabled) {
            return;
        }

        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

async function saveTicketEventForm(form) {
    if (form.dataset.ticketEventSaving === 'true') {
        return;
    }

    const component = livewireComponentFor(form);

    if (! component?.call) {
        return;
    }

    flushTicketEventFormInputs(form);
    const { activeIndex, payload, stagePayload } = prepareStadiumFormPayload(form);
    form.dataset.ticketEventSaving = 'true';
    form.querySelectorAll('[type="submit"]').forEach((button) => {
        button.disabled = true;
    });

    try {
        await component.call('save', payload, activeIndex, stagePayload);
    } catch (error) {
        if (error?.status === 422) {
            console.warn('No se pudo guardar el evento por errores de validacion.', error);
            window.setTimeout(() => {
                document.querySelector('[data-ticket-event-errors]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 120);
        }

        if (error?.status && error.status !== 422) {
            console.error(error);
        }
    } finally {
        delete form.dataset.ticketEventSaving;
        form.querySelectorAll('[type="submit"]').forEach((button) => {
            button.disabled = false;
        });
    }
}

function updateBlockFromInputs(editor, input) {
    const [index] = String(input.dataset.stadiumInput || '').split(':');
    const block = editor.querySelector(`[data-stadium-block][data-index="${index}"]`);

    if (! block) {
        return;
    }

    const x = Number(editor.querySelector(`[data-stadium-input="${index}:x"]`)?.value || block.dataset.x || 0);
    const y = Number(editor.querySelector(`[data-stadium-input="${index}:y"]`)?.value || block.dataset.y || 0);
    const w = Number(editor.querySelector(`[data-stadium-input="${index}:w"]`)?.value || block.dataset.w || 20);
    const h = Number(editor.querySelector(`[data-stadium-input="${index}:h"]`)?.value || block.dataset.h || 20);

    applyBlockGeometry(block, {
        ...snapStadiumGeometry({ x, y, w, h }),
    });
    rememberStadiumBlock(editor, block);
}

function updateBlockColor(editor, input) {
    const block = editor.querySelector(`[data-stadium-block][data-index="${input.dataset.stadiumColorInput}"]`);

    if (block) {
        block.style.background = input.value;
    }
}

function initStadiumEditor(editor) {
    restoreStadiumEditor(editor);
    restoreActiveStadiumPanel(editor);

    if (stadiumEditors.has(editor)) {
        return;
    }

    stadiumEditors.add(editor);

    let activeInteraction = null;

    editor.addEventListener('input', (event) => {
        if (event.target instanceof HTMLInputElement && event.target.dataset.stadiumInput) {
            updateBlockFromInputs(editor, event.target);
        }

        if (event.target instanceof HTMLInputElement && event.target.dataset.stadiumColorInput) {
            updateBlockColor(editor, event.target);
        }
    });

    editor.addEventListener('click', (event) => {
        if (! (event.target instanceof Element)) {
            return;
        }

        const trigger = event.target.closest('[data-stadium-accordion-trigger]');

        if (trigger) {
            activateStadiumPanel(editor, trigger.dataset.index, true);
            return;
        }

        const panel = event.target.closest('[data-stadium-panel]');

        if (panel) {
            activateStadiumPanel(editor, panel.dataset.index, false);
        }
    });

    editor.addEventListener('focusin', (event) => {
        if (! (event.target instanceof Element)) {
            return;
        }

        const panel = event.target.closest('[data-stadium-panel]');

        if (panel) {
            activateStadiumPanel(editor, panel.dataset.index, false);
        }
    });

    editor.addEventListener('pointerdown', (event) => {
        if (! (event.target instanceof Element)) {
            return;
        }

        const block = event.target.closest('[data-stadium-block]');
        const stageObject = event.target.closest('[data-stadium-stage-object]');
        const geometryTarget = block || stageObject;
        const stage = editor.querySelector('[data-stadium-stage]');

        if (! geometryTarget || ! stage) {
            return;
        }

        if (block) {
            activateStadiumPanel(editor, block.dataset.index, true);
        }

        event.preventDefault();
        geometryTarget.setPointerCapture?.(event.pointerId);
        const rect = stage.getBoundingClientRect();
        const start = readBlockGeometry(geometryTarget);

        activeInteraction = {
            block: geometryTarget,
            stage,
            rect,
            isStageObject: Boolean(stageObject),
            mode: event.target.closest('[data-stadium-resize], [data-stadium-stage-resize]') ? 'resize' : 'move',
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            start,
        };
    });

    editor.addEventListener('pointermove', (event) => {
        if (! activeInteraction || activeInteraction.pointerId !== event.pointerId) {
            return;
        }

        const dx = ((event.clientX - activeInteraction.startClientX) / activeInteraction.rect.width) * 100;
        const dy = ((event.clientY - activeInteraction.startClientY) / activeInteraction.rect.height) * 100;
        const next = { ...activeInteraction.start };

        if (activeInteraction.mode === 'resize') {
            next.w = clamp(activeInteraction.start.w + dx, 6, 100 - activeInteraction.start.x);
            next.h = clamp(activeInteraction.start.h + dy, 6, 100 - activeInteraction.start.y);
        } else {
            next.x = clamp(activeInteraction.start.x + dx, 0, 100 - activeInteraction.start.w);
            next.y = clamp(activeInteraction.start.y + dy, 0, 100 - activeInteraction.start.h);
        }

        applyBlockGeometry(activeInteraction.block, {
            ...snapStadiumGeometry(next),
        });
    });

    function finishInteraction(event) {
        if (! activeInteraction || activeInteraction.pointerId !== event.pointerId) {
            return;
        }

        if (activeInteraction.isStageObject) {
            syncStadiumStageInputs(editor, activeInteraction.block);
        } else {
            syncStadiumInputs(editor, activeInteraction.block);
        }
        if (activeInteraction.isStageObject) {
            rememberStadiumStage(editor);
        } else {
            rememberStadiumBlock(editor, activeInteraction.block);
        }
        activeInteraction = null;
    }

    editor.addEventListener('pointerup', finishInteraction);
    editor.addEventListener('pointercancel', finishInteraction);
}

document.addEventListener('click', async (event) => {
    if (! (event.target instanceof Element)) {
        return;
    }

    const button = event.target.closest('[data-stadium-add-block]');

    if (! button) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (button.disabled) {
        return;
    }

    const editor = document.querySelector('[data-stadium-editor]');
    const component = livewireComponentFor(button);

    button.disabled = true;

    try {
        if (editor) {
            await syncStadiumGeometryToLivewire(editor);
        }

        if (component?.call) {
            const nextIndex = editor ? editor.querySelectorAll('[data-stadium-block]').length : null;
            if (nextIndex !== null) {
                stadiumActivePanelCache.set(activePanelKey(editor), String(nextIndex));
                writeActiveStadiumPanel(editor, nextIndex);
            }

            await component.call('addZone');
        }
    } finally {
        button.disabled = false;
    }
}, true);

document.addEventListener('click', (event) => {
    if (! (event.target instanceof Element)) {
        return;
    }

    const saveButton = event.target.closest('[data-stadium-save-event]');

    if (saveButton) {
        const form = saveButton.closest('[data-ticket-event-form]');

        if (form instanceof HTMLFormElement) {
            event.preventDefault();
            event.stopImmediatePropagation();
            saveTicketEventForm(form);
            return;
        }
    }

    const submitter = event.target.closest('[type="submit"]');
    const form = submitter?.closest('[data-ticket-event-form]');

    if (form instanceof HTMLFormElement) {
        prepareStadiumFormPayload(form);
    }
}, true);

document.addEventListener('submit', (event) => {
    const form = event.target;

    if (! (form instanceof HTMLFormElement) || ! form.matches('[data-ticket-event-form]')) {
        return;
    }

    const editor = form.querySelector('[data-stadium-editor]');

    if (! editor) {
        return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    saveTicketEventForm(form);
}, true);

function initStadiumEditors(root = document) {
    root.querySelectorAll('[data-stadium-editor]').forEach((editor) => initStadiumEditor(editor));
}

function selectedPublicStadiumBlock(wrapper) {
    const checked = wrapper.querySelector('input[name="event_zone_id"]:checked');

    return checked?.closest('[data-public-stadium-block]') || null;
}

function writePublicCheckoutMoney(form, selector, value, showFree = false) {
    const target = form.querySelector(selector);

    if (target) {
        target.textContent = showFree && Number(value || 0) <= 0
            ? 'Gratis'
            : moneyFormatter.format(Number(value || 0));
    }
}

function roundedMoneyValue(value) {
    return Math.round(Number(value || 0) * 100) / 100;
}

function publicCheckoutMoney(value) {
    return Number(value || 0) <= 0 ? 'Gratis' : moneyFormatter.format(Number(value || 0));
}

function quantityTierQuote(priceSource, quantity) {
    if (! priceSource?.dataset?.quantityTiers) {
        return null;
    }

    let tiers;
    try {
        tiers = JSON.parse(priceSource.dataset.quantityTiers || '[]');
    } catch (_) {
        return null;
    }

    if (! Array.isArray(tiers) || tiers.length === 0) {
        return null;
    }

    const allocated = Math.max(0, Number(priceSource.dataset.quantityAllocated || 0));
    const basePrice = Number(priceSource.dataset.price || 0);
    const prices = Array.from({ length: quantity }, (_, index) => {
        const position = allocated + index + 1;
        const tier = tiers.find((candidate) => position >= Number(candidate.from || 0)
            && (candidate.to === null || candidate.to === '' || position <= Number(candidate.to)));

        return Number(tier?.price ?? basePrice);
    });

    return {
        subtotal: roundedMoneyValue(prices.reduce((sum, price) => sum + price, 0)),
        unitPrice: roundedMoneyValue(prices.reduce((sum, price) => sum + price, 0) / quantity),
        mixed: new Set(prices.map(String)).size > 1,
    };
}

function updatePublicCheckoutPaymentState(form, total, serviceFee) {
    const isFree = Number(total || 0) <= 0;
    const paymentMethod = form.querySelector('[data-public-payment-method]');
    const paymentTitle = form.querySelector('[data-public-payment-title]');
    const paymentCopy = form.querySelector('[data-public-payment-copy]');
    const choiceTitle = form.querySelector('[data-public-payment-choice-title]');
    const choiceCopy = form.querySelector('[data-public-payment-choice-copy]');
    const choiceBadge = form.querySelector('[data-public-payment-choice-badge]');
    const submit = form.querySelector('[data-public-payment-submit]');
    const checkoutBadge = form.querySelector('[data-public-checkout-badge]');
    const serviceFeeRow = form.querySelector('[data-public-service-fee-row]');

    if (paymentMethod instanceof HTMLInputElement) {
        paymentMethod.value = isFree ? 'free' : 'wallet';
    }

    if (paymentTitle) {
        paymentTitle.textContent = isFree ? 'Registro sin costo' : 'Medios de pago';
    }

    if (paymentCopy) {
        paymentCopy.textContent = isFree
            ? 'Completá tus datos y confirmá el registro. Las entradas se emiten sin pasar por Mercado Pago.'
            : 'Paga seguro con Mercado Pago. La plataforma retiene el cargo de servicio y acredita el importe del vendedor por marketplace.';
    }

    if (choiceTitle) {
        choiceTitle.textContent = isFree ? 'Entrada gratis' : 'Mercado Pago';
    }

    if (choiceCopy) {
        choiceCopy.textContent = isFree ? 'Registro inmediato con QR por email' : 'Pago online seguro';
    }

    if (choiceBadge) {
        choiceBadge.textContent = isFree ? 'Sin costo' : 'Seleccionado';
    }

    if (submit) {
        submit.textContent = isFree ? 'Obtener entradas' : 'Pagar con Mercado Pago';
    }

    if (checkoutBadge) {
        checkoutBadge.textContent = isFree ? 'Registro gratis' : 'Pago seguro';
    }

    if (serviceFeeRow) {
        serviceFeeRow.classList.toggle('hidden', isFree || Number(serviceFee || 0) <= 0);
    }
}

function publicCheckoutQuantity(form) {
    const input = form.querySelector('[data-public-quantity]');
    const value = Number(input?.value || 1);
    const max = Number(input?.getAttribute('max') || 10);

    return clamp(Number.isFinite(value) ? Math.round(value) : 1, 1, Number.isFinite(max) ? max : 10);
}

function writePublicCheckoutQuantity(form, quantity) {
    const input = form.querySelector('[data-public-quantity]');
    const max = Number(input?.getAttribute('max') || 10);
    const normalizedQuantity = clamp(Number.isFinite(Number(quantity)) ? Math.round(Number(quantity)) : 1, 1, Number.isFinite(max) ? max : 10);

    if (input instanceof HTMLInputElement) {
        input.value = String(normalizedQuantity);
    }

    return normalizedQuantity;
}

function selectedPublicTicketOption(form) {
    const stadiumWrapper = form.querySelector('[data-public-stadium-checkout]');
    const stadiumBlock = stadiumWrapper ? selectedPublicStadiumBlock(stadiumWrapper) : null;

    if (stadiumBlock) {
        return stadiumBlock;
    }

    const checked = form.querySelector('input[name="price_window_id"]:checked')
        || form.querySelector('input[name="ticket_type_id"]:checked');

    return checked?.closest('[data-public-ticket-option]') || form.querySelector('[data-public-ticket-option]');
}

function selectedPublicTicketTypeId(form) {
    const selectedOption = selectedPublicTicketOption(form);

    return selectedOption?.dataset.ticketTypeId
        || form.querySelector('input[name="ticket_type_id"]:checked')?.value
        || form.querySelector('[data-public-price-window-ticket-type]')?.value
        || '';
}

function visibleTheaterSeatInputs(wrapper) {
    return Array.from(wrapper.querySelectorAll('input[name="seat_ids[]"]') || [])
        .filter((input) => {
            const seatOption = input.closest('[data-theater-seat-option]');
            const row = input.closest('[data-theater-row]');
            const zone = input.closest('[data-theater-zone]');

            return ! seatOption?.classList.contains('hidden')
                && ! row?.classList.contains('hidden')
                && ! zone?.classList.contains('hidden');
        })
        .filter((input) => input instanceof HTMLInputElement);
}

function availableTheaterSeatInputs(wrapper) {
    return visibleTheaterSeatInputs(wrapper)
        .filter((input) => input.dataset.seatOccupied !== 'true');
}

function applyTheaterSeatQuantity(wrapper, desiredQuantity) {
    const quantity = clamp(Number.isFinite(Number(desiredQuantity)) ? Math.round(Number(desiredQuantity)) : 1, 1, 10);
    const inputs = availableTheaterSeatInputs(wrapper);
    const selected = inputs.filter((input) => input.checked);
    const unselected = inputs.filter((input) => ! input.checked);
    const nextSelected = [...selected, ...unselected].slice(0, Math.min(quantity, inputs.length));

    inputs.forEach((input) => {
        input.checked = nextSelected.includes(input);
    });

    return nextSelected.length;
}

function normalizeManualTheaterSeats(wrapper) {
    const inputs = availableTheaterSeatInputs(wrapper);
    const selected = inputs.filter((input) => input.checked);

    if (selected.length === 0) {
        return applyTheaterSeatQuantity(wrapper, 1);
    }

    selected.slice(10).forEach((input) => {
        input.checked = false;
    });

    return Math.min(selected.length, 10);
}

function syncPublicTheaterZones(form, source = 'general') {
    const wrapper = form.querySelector('[data-public-theater-checkout]');

    if (! wrapper) {
        return;
    }

    const selectedTicketTypeId = selectedPublicTicketTypeId(form);
    const zoneInput = wrapper.querySelector('[data-public-theater-zone-input]');
    let firstVisibleZoneId = '';

    wrapper.querySelectorAll('[data-theater-zone]').forEach((zone) => {
        let zoneHasVisibleSeats = false;

        zone.querySelectorAll('[data-theater-row]').forEach((row) => {
            let rowHasVisibleSeats = false;

            row.querySelectorAll('[data-theater-seat-option]').forEach((seatOption) => {
                const input = seatOption.querySelector('input[name="seat_ids[]"]');

                if (! (input instanceof HTMLInputElement)) {
                    return;
                }

                const isVisible = input.dataset.ticketTypeId === selectedTicketTypeId;
                const isOccupied = input.dataset.seatOccupied === 'true';

                seatOption.classList.toggle('hidden', ! isVisible);
                input.disabled = isOccupied || ! isVisible;

                if (! isVisible) {
                    input.checked = false;
                } else {
                    rowHasVisibleSeats = true;
                    zoneHasVisibleSeats = true;
                }
            });

            row.classList.toggle('hidden', ! rowHasVisibleSeats);
        });

        zone.classList.toggle('hidden', ! zoneHasVisibleSeats);

        if (zoneHasVisibleSeats && ! firstVisibleZoneId) {
            firstVisibleZoneId = zone.dataset.zoneId || '';
        }
    });

    if (zoneInput instanceof HTMLInputElement) {
        zoneInput.value = firstVisibleZoneId;
    }

    const desiredQuantity = publicCheckoutQuantity(form);
    const selectedQuantity = source === 'seat'
        ? normalizeManualTheaterSeats(wrapper)
        : applyTheaterSeatQuantity(wrapper, desiredQuantity);

    writePublicCheckoutQuantity(form, selectedQuantity > 0 ? selectedQuantity : desiredQuantity);
}

function selectedTheaterSeatTotals(form, fallbackUnitPrice) {
    const wrapper = form.querySelector('[data-public-theater-checkout]');

    if (! wrapper) {
        return null;
    }

    const selectedSeats = visibleTheaterSeatInputs(wrapper)
        .filter((input) => input.checked);

    if (selectedSeats.length === 0) {
        return null;
    }

    const seats = selectedSeats.map((input) => {
        const option = input.closest('[data-theater-seat-option]');
        const price = Number(input.dataset.price || option?.dataset.price || fallbackUnitPrice || 0);

        return {
            label: option?.dataset.seatLabel || `Butaca ${input.value}`,
            category: option?.dataset.seatCategory || 'Entrada',
            zone: option?.dataset.zoneName || '',
            row: option?.dataset.rowLabel || '',
            price,
            priceLabel: option?.dataset.priceLabel || publicCheckoutMoney(price),
        };
    });
    const subtotal = seats.reduce((total, seat) => total + seat.price, 0);

    return {
        seats,
        quantity: seats.length,
        unitPrice: roundedMoneyValue(subtotal / seats.length),
        subtotal: roundedMoneyValue(subtotal),
    };
}

function updateTheaterPricePreview(form, theaterTotals, fallbackUnitPrice) {
    const wrapper = form.querySelector('[data-public-theater-checkout]');

    if (! wrapper) {
        return;
    }

    const previewSeat = wrapper.querySelector('[data-theater-preview-seat]');
    const previewMeta = wrapper.querySelector('[data-theater-preview-meta]');
    const previewPrice = wrapper.querySelector('[data-theater-preview-price]');
    const selectedSeatsBox = form.querySelector('[data-public-selected-seats]');
    const selectedSeatsList = form.querySelector('[data-public-selected-seats-list]');
    const seats = theaterTotals?.seats || [];
    const lastSeat = seats[seats.length - 1] || null;

    if (previewSeat) {
        previewSeat.textContent = lastSeat ? lastSeat.label : 'Elegí una butaca';
    }

    if (previewMeta) {
        previewMeta.textContent = lastSeat
            ? `${lastSeat.category} · ${lastSeat.zone} · fila ${lastSeat.row}`
            : 'El precio se actualiza según categoria, sector y fila.';
    }

    if (previewPrice) {
        const previousPrice = wrapper.dataset.previewPrice || '';
        const nextPrice = lastSeat ? String(lastSeat.price) : String(fallbackUnitPrice || 0);

        previewPrice.textContent = lastSeat ? lastSeat.priceLabel : publicCheckoutMoney(fallbackUnitPrice);

        if (previousPrice !== '' && previousPrice !== nextPrice) {
            previewPrice.classList.remove('ep-price-flash');
            window.requestAnimationFrame(() => previewPrice.classList.add('ep-price-flash'));
        }

        wrapper.dataset.previewPrice = nextPrice;
    }

    if (selectedSeatsBox) {
        selectedSeatsBox.classList.toggle('hidden', seats.length === 0);
    }

    if (selectedSeatsList) {
        selectedSeatsList.replaceChildren(...seats.map((seat) => {
            const row = document.createElement('div');
            row.className = 'flex items-start justify-between gap-3 rounded-md bg-[#f7f9f4] px-3 py-2';

            const label = document.createElement('span');
            label.className = 'min-w-0';

            const title = document.createElement('strong');
            title.className = 'block truncate text-zinc-950';
            title.textContent = seat.label;

            const meta = document.createElement('span');
            meta.className = 'block truncate text-xs font-semibold text-zinc-500';
            meta.textContent = `${seat.category} · ${seat.zone} · fila ${seat.row}`;

            const price = document.createElement('strong');
            price.className = 'shrink-0 text-zinc-950';
            price.textContent = seat.priceLabel;

            label.append(title, meta);
            row.append(label, price);

            return row;
        }));
    }
}

function syncPublicCheckout(form, trigger = 'general') {
    const stadiumWrapper = form.querySelector('[data-public-stadium-checkout]');
    const block = stadiumWrapper ? selectedPublicStadiumBlock(stadiumWrapper) : null;
    const priceSource = block || selectedPublicTicketOption(form);

    if (! priceSource) {
        return;
    }

    if (block) {
        const ticketTypeInput = form.querySelector('[data-public-stadium-ticket-type]');

        if (ticketTypeInput instanceof HTMLInputElement && block.dataset.ticketTypeId) {
            ticketTypeInput.value = block.dataset.ticketTypeId;
        }

        const selectedZone = stadiumWrapper.querySelector('[data-public-stadium-selected-zone]');

        if (selectedZone) {
            selectedZone.textContent = block.dataset.zoneName || 'Selecciona un bloque';
        }
    }

    const selectedTicketTypeId = selectedPublicTicketTypeId(form);
    const priceWindowTicketTypeInput = form.querySelector('[data-public-price-window-ticket-type]');

    if (priceWindowTicketTypeInput instanceof HTMLInputElement && selectedTicketTypeId) {
        priceWindowTicketTypeInput.value = selectedTicketTypeId;
    }

    syncPublicTheaterZones(form, trigger);

    let quantity = publicCheckoutQuantity(form);
    let unitPrice = Number(priceSource.dataset.price || 0);
    const serviceFeeType = form.dataset.serviceFeeType || 'percent';
    const serviceFeeValue = Number(form.dataset.serviceFeeValue ?? form.dataset.serviceFeePercent ?? 0);
    const serviceFeePlacement = form.dataset.serviceFeePlacement || 'outside';
    const theaterTotals = selectedTheaterSeatTotals(form, unitPrice);
    const tierQuote = theaterTotals ? null : quantityTierQuote(priceSource, quantity);

    if (theaterTotals) {
        quantity = theaterTotals.quantity;
        unitPrice = theaterTotals.unitPrice;
    }

    const subtotal = theaterTotals?.subtotal ?? tierQuote?.subtotal ?? roundedMoneyValue(unitPrice * quantity);
    if (tierQuote) {
        unitPrice = tierQuote.unitPrice;
    }
    const calculatedServiceFee = subtotal <= 0
        ? 0
        : (serviceFeeType === 'fixed'
            ? roundedMoneyValue(serviceFeeValue * quantity)
            : roundedMoneyValue(subtotal * (serviceFeeValue / 100)));
    const serviceFee = serviceFeePlacement === 'included'
        ? Math.min(subtotal, calculatedServiceFee)
        : calculatedServiceFee;
    const total = serviceFeePlacement === 'included'
        ? roundedMoneyValue(subtotal)
        : roundedMoneyValue(subtotal + serviceFee);
    const quantityLabel = form.querySelector('[data-public-quantity-label]');

    if (quantityLabel) {
        quantityLabel.textContent = `x ${quantity}`;
    }

    writePublicCheckoutMoney(form, '[data-public-unit-price]', unitPrice, true);
    writePublicCheckoutMoney(form, '[data-public-subtotal]', subtotal, true);
    writePublicCheckoutMoney(form, '[data-public-service-fee]', serviceFee);
    writePublicCheckoutMoney(form, '[data-public-total]', total, true);
    const tierNote = form.querySelector('[data-public-quantity-tier-note]');
    if (tierNote) {
        tierNote.classList.toggle('hidden', ! tierQuote?.mixed);
        if (tierQuote?.mixed) {
            tierNote.textContent = 'El subtotal combina escalones de precio por cantidad.';
        }
    }
    updateTheaterPricePreview(form, theaterTotals, unitPrice);
    updatePublicCheckoutPaymentState(form, total, serviceFee);
}

function startPublicCheckoutSendProgress(form) {
    const modal = form.querySelector('[data-ticket-send-modal]');
    const progress = form.querySelector('[data-ticket-send-progress]');
    const title = form.querySelector('[data-ticket-send-title]');
    const label = form.querySelector('[data-ticket-send-label]');
    const firstStep = form.querySelector('[data-ticket-send-first-step]');
    const submit = form.querySelector('button[type="submit"]');
    const paymentMethod = form.querySelector('[data-public-payment-method]');
    const isFree = paymentMethod instanceof HTMLInputElement && paymentMethod.value === 'free';

    if (! modal || ! progress) {
        return;
    }

    if (publicCheckoutSendTimers.has(form)) {
        return;
    }

    const steps = isFree
        ? ['Registrando inscripcion', 'Generando entradas', 'Creando codigos QR', 'Enviando email']
        : ['Registrando compra', 'Generando entradas', 'Creando codigos QR', 'Enviando email'];
    let value = 12;
    let stepIndex = 0;

    if (title) {
        title.textContent = isFree ? 'Procesando registro' : 'Procesando compra';
    }

    if (label) {
        label.textContent = steps[0];
    }

    if (firstStep) {
        firstStep.textContent = isFree ? 'Registro' : 'Compra';
    }

    modal.classList.remove('hidden');
    modal.classList.add('flex');
    document.body.classList.add('overflow-hidden');

    if (submit instanceof HTMLButtonElement) {
        submit.disabled = true;
        submit.setAttribute('aria-disabled', 'true');
    }

    progress.style.width = `${value}%`;

    const timer = window.setInterval(() => {
        value = Math.min(94, value + Math.max(2, Math.round((94 - value) * 0.18)));
        progress.style.width = `${value}%`;

        if (label && stepIndex < steps.length - 1 && value >= 24 + (stepIndex * 20)) {
            stepIndex += 1;
            label.textContent = steps[stepIndex];
        }
    }, 360);

    publicCheckoutSendTimers.set(form, timer);
}

function initPublicCheckout(form) {
    syncPublicCheckout(form);

    if (publicCheckouts.has(form)) {
        return;
    }

    publicCheckouts.add(form);

    form.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) {
            return;
        }

        if (event.target instanceof HTMLElement && event.target.closest('[data-public-payment-submit]')) {
            return;
        }

        event.preventDefault();
    });

    form.addEventListener('input', (event) => {
        if (event.target instanceof HTMLInputElement && event.target.matches('[data-public-quantity]')) {
            syncPublicCheckout(form, 'quantity');
        }
    });

    form.addEventListener('change', (event) => {
        if (! (event.target instanceof HTMLInputElement)) {
            return;
        }

        if (event.target.name === 'seat_ids[]') {
            syncPublicCheckout(form, 'seat');
            return;
        }

        if (event.target.name === 'quantity') {
            syncPublicCheckout(form, 'quantity');
            return;
        }

        if (event.target.name === 'ticket_type_id' || event.target.name === 'price_window_id') {
            syncPublicCheckout(form, 'ticket_type');
            return;
        }

        if (event.target.name === 'event_zone_id') {
            syncPublicCheckout(form, 'zone');
        }
    });

    form.addEventListener('submit', (event) => {
        if (event.defaultPrevented) {
            return;
        }

        startPublicCheckoutSendProgress(form);
    });
}

function initPublicCheckouts(root = document) {
    root.querySelectorAll('[data-public-checkout]').forEach((form) => initPublicCheckout(form));
}

function qrScannerStatus(root, message, tone = 'muted') {
    const target = root.querySelector('[data-qr-status]');

    if (! target) {
        return;
    }

    target.textContent = message;
    target.className = 'rounded-lg border p-2 text-xs font-semibold sm:p-3 sm:text-sm';

    if (tone === 'ok') {
        target.classList.add('border-green-200', 'bg-green-50', 'text-green-800', 'dark:border-green-900/40', 'dark:bg-green-950/20', 'dark:text-green-100');
        return;
    }

    if (tone === 'bad') {
        target.classList.add('border-red-200', 'bg-red-50', 'text-red-800', 'dark:border-red-900/40', 'dark:bg-red-950/20', 'dark:text-red-100');
        return;
    }

    target.classList.add('border-zinc-200', 'bg-zinc-50', 'text-zinc-600', 'dark:border-white/10', 'dark:bg-white/5', 'dark:text-zinc-300');
}

function setQrScannerActive(root, active) {
    const start = root.querySelector('[data-qr-start]');
    const stop = root.querySelector('[data-qr-stop]');
    const placeholder = root.querySelector('[data-qr-placeholder]');

    start?.toggleAttribute('disabled', active);
    stop?.toggleAttribute('disabled', ! active);
    start?.setAttribute('aria-disabled', active ? 'true' : 'false');
    stop?.setAttribute('aria-disabled', active ? 'false' : 'true');
    placeholder?.classList.toggle('hidden', active);
}

function stopQrScanner(root, message = null, tone = 'muted') {
    const state = qrScannerStates.get(root);
    const video = root.querySelector('[data-qr-video]');

    if (state?.frame) {
        window.cancelAnimationFrame(state.frame);
    }

    if (state?.popupTimer) {
        window.clearTimeout(state.popupTimer);
    }

    state?.stream?.getTracks().forEach((track) => track.stop());

    if (video instanceof HTMLVideoElement) {
        video.pause();
        video.srcObject = null;
    }

    qrScannerStates.delete(root);
    hideQrScannerPopup(root);
    setQrScannerActive(root, false);

    if (message) {
        qrScannerStatus(root, message, tone);
    }
}

function scheduleQrScannerFrame(root, state = qrScannerStates.get(root)) {
    if (! state) {
        return;
    }

    state.frame = window.requestAnimationFrame(() => scanQrFrame(root));
}

function resumeQrScanner(root, code = null) {
    const state = qrScannerStates.get(root);

    if (! state) {
        return;
    }

    state.locked = false;
    state.lastSubmittedCode = code ? String(code).trim() : state.lastSubmittedCode;
    state.clearSince = null;
    scheduleQrScannerFrame(root, state);
}

function showQrScannerPopup(root, payload, tone = null) {
    const target = root.querySelector('[data-qr-popup]');

    if (! target) {
        return;
    }

    const state = qrScannerStates.get(root);
    if (state?.popupTimer) {
        window.clearTimeout(state.popupTimer);
        state.popupTimer = null;
    }

    const ok = tone === 'ok' || payload?.state === 'ok';
    const bad = tone === 'bad' || payload?.state === 'bad';
    const title = payload?.title || (ok ? 'Ingreso autorizado' : bad ? 'QR rechazado' : 'Validando ingreso');
    const message = payload?.message || (bad ? 'La entrada no esta habilitada.' : 'Consultando estado de la entrada...');

    target.replaceChildren();
    target.className = 'pointer-events-none absolute inset-0 z-10 grid place-items-center bg-zinc-950/55 p-4 backdrop-blur-sm';

    const panel = document.createElement('div');
    panel.className = ok
        ? 'w-full max-w-sm rounded-xl border-4 border-green-300 bg-green-50 p-5 text-center text-green-950 shadow-2xl dark:border-green-600 dark:bg-green-950 dark:text-green-50'
        : bad
            ? 'w-full max-w-sm rounded-xl border-4 border-red-300 bg-red-50 p-5 text-center text-red-950 shadow-2xl dark:border-red-600 dark:bg-red-950 dark:text-red-50'
            : 'w-full max-w-sm rounded-xl border-4 border-lime-300 bg-zinc-50 p-5 text-center text-zinc-950 shadow-2xl dark:border-lime-400 dark:bg-zinc-950 dark:text-white';

    const label = document.createElement('span');
    label.className = 'block text-xs font-black uppercase';
    label.textContent = bad ? 'Escaneo rechazado' : ok ? 'Escaneo aprobado' : 'Escaneo detectado';

    const heading = document.createElement('strong');
    heading.className = 'mt-2 block text-2xl font-black';
    heading.textContent = title;

    const detail = document.createElement('span');
    detail.className = 'mt-2 block text-sm font-semibold';
    detail.textContent = message;

    panel.append(label, heading, detail);
    target.appendChild(panel);
}

function hideQrScannerPopup(root) {
    const target = root.querySelector('[data-qr-popup]');

    if (! target) {
        return;
    }

    target.replaceChildren();
    target.className = 'hidden';
}

function releaseQrScannerAfterResult(root, code = null) {
    const state = qrScannerStates.get(root);

    if (! state) {
        return;
    }

    if (state.popupTimer) {
        window.clearTimeout(state.popupTimer);
    }

    state.popupTimer = window.setTimeout(() => {
        state.popupTimer = null;
        hideQrScannerPopup(root);
        resumeQrScanner(root, code);
    }, QR_SCANNER_RESULT_HOLD_MS);
}

function readQrScannerFrame(root, force = false) {
    const state = qrScannerStates.get(root);
    const video = root.querySelector('[data-qr-video]');
    const canvas = root.querySelector('[data-qr-canvas]');

    if (! state || ! (video instanceof HTMLVideoElement) || ! (canvas instanceof HTMLCanvasElement)) {
        return 'inactive';
    }

    if (state.locked) {
        return 'locked';
    }

    if (video.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA || video.videoWidth <= 0 || video.videoHeight <= 0) {
        return 'waiting';
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (! context) {
        return 'waiting';
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const result = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'attemptBoth' });
    const resultCode = String(result?.data || '').trim();

    if (resultCode) {
        state.clearSince = null;
    } else if (state.lastSubmittedCode) {
        state.clearSince ||= Date.now();

        if (Date.now() - state.clearSince >= QR_SCANNER_CLEAR_REPEAT_MS) {
            state.lastSubmittedCode = null;
            state.clearSince = null;
        }
    }

    if (! resultCode) {
        return 'empty';
    }

    if (! force && resultCode === state.lastSubmittedCode) {
        return 'repeat';
    }

    state.locked = true;
    state.lastSubmittedCode = resultCode;
    submitQrScannerCode(root, resultCode);

    return 'submitted';
}

function forceQrScannerRead(root) {
    const state = qrScannerStates.get(root);

    if (! state) {
        qrScannerStatus(root, 'Abriendo camara para escanear...', 'muted');
        startQrScanner(root);
        return;
    }

    if (state.locked) {
        qrScannerStatus(root, 'Escaneo en proceso. Espera el resultado.', 'muted');
        return;
    }

    if (state.frame) {
        window.cancelAnimationFrame(state.frame);
        state.frame = null;
    }

    const result = readQrScannerFrame(root, true);

    if (result === 'submitted') {
        return;
    }

    if (result === 'waiting') {
        qrScannerStatus(root, 'Camara activa. Esperando imagen para escanear.', 'muted');
    } else if (result === 'empty') {
        qrScannerStatus(root, 'No encontramos un QR en este cuadro. Acerca o enfoca la entrada.', 'muted');
    }

    scheduleQrScannerFrame(root, state);
}

function qrScannerCodeInput(root) {
    const componentRoot = root.closest('[wire\\:id]');

    return componentRoot?.querySelector('[data-qr-code-input]') || null;
}

function qrScannerGate(root) {
    const componentRoot = root.closest('[wire\\:id]');
    const target = componentRoot?.querySelector('[data-qr-gate-input]');
    const input = target instanceof HTMLInputElement ? target : target?.querySelector?.('input');

    return input instanceof HTMLInputElement ? input.value : '';
}

function qrScannerPerformanceId(root) {
    const componentRoot = root.closest('[wire\\:id]');
    const target = componentRoot?.querySelector('[data-qr-performance-input]');
    const input = target instanceof HTMLSelectElement ? target : target?.querySelector?.('select');
    const value = input instanceof HTMLSelectElement ? input.value : (root.dataset.qrPerformanceId || '');

    return value !== '' ? Number(value) : null;
}

function qrScannerCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

function renderQrScannerResult(root, payload) {
    const target = root.querySelector('[data-qr-inline-result]');

    if (! target) {
        return;
    }

    const ok = payload?.state === 'ok';
    target.replaceChildren();
    target.className = ok
        ? 'rounded-lg border border-green-200 bg-green-50 p-3 text-green-900 dark:border-green-900/40 dark:bg-green-950/20 dark:text-green-100'
        : 'rounded-lg border border-red-200 bg-red-50 p-3 text-red-900 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-100';

    const title = document.createElement('span');
    title.className = 'text-sm font-black uppercase';
    title.textContent = payload?.title || (ok ? 'Ingreso autorizado' : 'QR rechazado');
    target.appendChild(title);

    const message = document.createElement('strong');
    message.className = 'mt-1 block text-sm lg:text-base';
    message.textContent = payload?.message || '';
    target.appendChild(message);

    const entries = Object.entries(payload?.meta || {});
    if (entries.length === 0) {
        return;
    }

    const list = document.createElement('dl');
    list.className = 'mt-2 grid grid-cols-2 gap-1.5 text-xs lg:mt-3 lg:gap-2 lg:text-sm';

    entries.forEach(([label, value]) => {
        const item = document.createElement('div');
        item.className = 'min-w-0 rounded-md bg-white/60 p-1.5 lg:p-2 dark:bg-white/5';

        const term = document.createElement('dt');
        term.className = 'text-xs font-black uppercase opacity-60';
        term.textContent = label;

        const description = document.createElement('dd');
        description.className = 'truncate font-semibold';
        description.textContent = value || '-';

        item.append(term, description);
        list.appendChild(item);
    });

    target.appendChild(list);
}

function clearQrScannerResult(root) {
    const target = root.querySelector('[data-qr-inline-result]');

    if (! target) {
        return;
    }

    target.replaceChildren();
    target.className = 'hidden';
}

async function submitQrScannerEndpoint(root, code) {
    const endpoint = root.dataset.qrValidateUrl;

    if (! endpoint) {
        return false;
    }

    const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': qrScannerCsrfToken(),
        },
        body: JSON.stringify({
            code,
            gate: qrScannerGate(root),
            performance_id: qrScannerPerformanceId(root),
        }),
    });

    const payload = await response.json().catch(() => null);

    if (! response.ok || ! payload) {
        throw new Error('QR validation request failed');
    }

    renderQrScannerResult(root, payload);
    showQrScannerPopup(root, payload);
    qrScannerStatus(root, payload.state === 'ok' ? 'Ingreso autorizado. Siguiente lectura en 2 segundos.' : `${payload.title}. Siguiente lectura en 2 segundos.`, payload.state === 'ok' ? 'ok' : 'bad');

    return true;
}

async function submitQrScannerCode(root, code) {
    const normalizedCode = String(code || '').trim();

    if (! normalizedCode) {
        return;
    }

    qrScannerStatus(root, `QR leido: ${normalizedCode}. Validando ingreso...`, 'ok');
    showQrScannerPopup(root, {
        title: 'Validando ingreso',
        message: 'Consultando estado de la entrada...',
    });
    clearQrScannerResult(root);

    const input = qrScannerCodeInput(root);
    if (input instanceof HTMLInputElement) {
        input.value = normalizedCode;
    }

    try {
        try {
            if (await submitQrScannerEndpoint(root, normalizedCode)) {
                return;
            }
        } catch (error) {
            showQrScannerPopup(root, {
                title: 'No se pudo validar',
                message: 'Revisa la conexion o usa la carga manual.',
            }, 'bad');
            qrScannerStatus(root, 'No se pudo validar el QR desde el endpoint. Siguiente lectura en 2 segundos.', 'bad');
            return;
        }

        const component = livewireComponentFor(root);
        if (! component?.call) {
            showQrScannerPopup(root, {
                title: 'QR leido',
                message: 'Presiona Validar QR para completar el ingreso.',
            });
            qrScannerStatus(root, 'QR leido. Presiona Validar QR para completar el ingreso.', 'ok');
            return;
        }

        try {
            if (component.set) {
                await component.set('code', normalizedCode);
            }

            await component.call('validateQr');
            showQrScannerPopup(root, {
                title: 'Validacion enviada',
                message: 'Resultado disponible en pantalla.',
            }, 'ok');
            qrScannerStatus(root, 'Validacion enviada. Siguiente lectura en 2 segundos.', 'ok');
        } catch (error) {
            showQrScannerPopup(root, {
                title: 'No se pudo validar',
                message: 'Intenta con el boton Validar QR.',
            }, 'bad');
            qrScannerStatus(root, 'No se pudo validar el QR desde el scanner. Siguiente lectura en 2 segundos.', 'bad');
        }
    } finally {
        releaseQrScannerAfterResult(root, normalizedCode);
    }
}

function scanQrFrame(root) {
    const state = qrScannerStates.get(root);

    if (! state) {
        return;
    }

    state.frame = null;

    const result = readQrScannerFrame(root);

    if (result === 'submitted' || result === 'inactive') {
        return;
    }

    scheduleQrScannerFrame(root, state);
}

async function startQrScanner(root) {
    if (qrScannerStates.has(root)) {
        return;
    }

    clearQrScannerResult(root);

    if (! navigator.mediaDevices?.getUserMedia) {
        qrScannerStatus(root, 'Este navegador no permite abrir la camara. Usa HTTPS o ingresa el codigo manualmente.', 'bad');
        return;
    }

    const video = root.querySelector('[data-qr-video]');
    if (! (video instanceof HTMLVideoElement)) {
        return;
    }

    try {
        qrScannerStatus(root, 'Solicitando permiso de camara...', 'muted');

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                facingMode: { ideal: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 },
            },
        });

        video.srcObject = stream;
        await video.play();

        qrScannerStates.set(root, {
            stream,
            frame: null,
            locked: false,
            lastSubmittedCode: null,
            clearSince: null,
            popupTimer: null,
        });

        setQrScannerActive(root, true);
        qrScannerStatus(root, 'Camara activa. Apunta al QR de una entrada habilitada.', 'ok');
        scanQrFrame(root);
    } catch (error) {
        stopQrScanner(root);
        qrScannerStatus(root, 'No pudimos abrir la camara. Revisa permisos del navegador o usa carga manual.', 'bad');
    }
}

function initQrScanner(root) {
    if (qrScanners.has(root)) {
        return;
    }

    qrScanners.add(root);

    root.querySelector('[data-qr-start]')?.addEventListener('click', () => startQrScanner(root));
    root.querySelector('[data-qr-stop]')?.addEventListener('click', () => stopQrScanner(root, 'Camara detenida.', 'muted'));
    root.querySelector('[data-qr-force-scan]')?.addEventListener('pointerup', () => forceQrScannerRead(root));

    if (root.dataset.qrAutostart === 'true') {
        window.setTimeout(() => startQrScanner(root), 250);
    }
}

function initQrScanners(root = document) {
    root.querySelectorAll('[data-qr-scanner]').forEach((scanner) => initQrScanner(scanner));
}

function eventLocationMarkerIcon() {
    return L.divIcon({
        className: '',
        html: '<span class="block size-5 rounded-full border-4 border-white bg-[#4010a0] shadow-lg ring-2 ring-[#ffc000]"></span>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
    });
}

function writeEventLocation(mapElement, latlng, marker = null) {
    const form = mapElement.closest('[data-ticket-event-form]');
    const latInput = form?.querySelector('[data-event-location-lat]');
    const lngInput = form?.querySelector('[data-event-location-lng]');
    const coordinateLabel = form?.querySelector('[data-event-location-coordinates]');
    const lat = Number(latlng.lat).toFixed(7);
    const lng = Number(latlng.lng).toFixed(7);

    if (latInput instanceof HTMLInputElement) {
        latInput.value = lat;
        latInput.dispatchEvent(new Event('input', { bubbles: true }));
        latInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (lngInput instanceof HTMLInputElement) {
        lngInput.value = lng;
        lngInput.dispatchEvent(new Event('input', { bubbles: true }));
        lngInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (coordinateLabel) {
        coordinateLabel.textContent = `${lat}, ${lng}`;
    }

    if (marker) {
        marker.setLatLng(latlng);
    }
}

function initEventLocationMap(mapElement) {
    const existing = eventLocationMaps.get(mapElement);

    if (existing) {
        window.setTimeout(() => existing.map.invalidateSize(), 80);
        return;
    }

    const initialLat = Number(mapElement.dataset.lat || 0);
    const initialLng = Number(mapElement.dataset.lng || 0);
    const hasInitialPoint = Number.isFinite(initialLat) && Number.isFinite(initialLng) && (initialLat !== 0 || initialLng !== 0);
    const initialCenter = hasInitialPoint ? [initialLat, initialLng] : [-24.7829, -65.4232];
    const map = L.map(mapElement, {
        zoomControl: true,
        scrollWheelZoom: false,
    }).setView(initialCenter, hasInitialPoint ? 15 : 12);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    let marker = hasInitialPoint
        ? L.marker(initialCenter, {
            draggable: true,
            icon: eventLocationMarkerIcon(),
        }).addTo(map)
        : null;

    const placeMarker = (latlng) => {
        if (! marker) {
            marker = L.marker(latlng, {
                draggable: true,
                icon: eventLocationMarkerIcon(),
            }).addTo(map);
            marker.on('dragend', () => writeEventLocation(mapElement, marker.getLatLng(), marker));
        }

        writeEventLocation(mapElement, latlng, marker);
    };

    marker?.on('dragend', () => writeEventLocation(mapElement, marker.getLatLng(), marker));
    map.on('click', (event) => placeMarker(event.latlng));
    eventLocationMaps.set(mapElement, { map, marker });
    window.setTimeout(() => map.invalidateSize(), 80);
}

function initEventLocationMaps(root = document) {
    root.querySelectorAll('[data-event-location-map]').forEach((mapElement) => initEventLocationMap(mapElement));
}

function initTicketPlatformUi(root = document) {
    initTicketCharts(root);
    initStadiumEditors(root);
    initEventLocationMaps(root);
    initPublicCheckouts(root);
    initQrScanners(root);
}

document.addEventListener('DOMContentLoaded', () => initTicketPlatformUi());
document.addEventListener('livewire:navigated', () => initTicketPlatformUi());
document.addEventListener('pointerdown', rememberAllStadiumEditors, true);

let ticketPlatformMutationFrame = null;

function scheduleTicketPlatformUiInit() {
    if (ticketPlatformMutationFrame) {
        return;
    }

    ticketPlatformMutationFrame = window.requestAnimationFrame(() => {
        ticketPlatformMutationFrame = null;
        initTicketPlatformUi();
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const observer = new MutationObserver((mutations) => {
        const shouldInit = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
            if (! (node instanceof Element)) {
                return false;
            }

            return node.matches('[data-ticket-chart], [data-stadium-editor], [data-event-location-map], [data-public-checkout], [data-qr-scanner]')
                || Boolean(node.querySelector('[data-ticket-chart], [data-stadium-editor], [data-event-location-map], [data-public-checkout], [data-qr-scanner]'));
        }));

        if (shouldInit) {
            scheduleTicketPlatformUiInit();
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });
});

function refreshTicketCharts(charts = {}) {
    Object.entries(charts).forEach(([chartId, config]) => {
        const wrapper = document.querySelector(`[data-ticket-chart][data-chart-id="${chartId}"]`);

        if (wrapper) {
            renderChart(wrapper, config);
        }
    });
}

window.addEventListener('ticket-platform:charts-refresh', (event) => {
    refreshTicketCharts(event.detail?.charts || {});
});

document.addEventListener('modal-show', (event) => {
    if (! event.detail?.name) {
        return;
    }

    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            const modal = document.querySelector(`[data-modal="${CSS.escape(event.detail.name)}"]`);

            if (modal) {
                initTicketCharts(modal);
                resizeTicketCharts(modal);
            }
        });
    });
});

document.addEventListener('livewire:init', () => {
    window.Livewire?.on('ticket-platform:charts-refresh', (event) => {
        refreshTicketCharts(event?.charts || event?.[0]?.charts || {});
    });
});

window.TicketPlatformCharts = {
    init: initTicketCharts,
    refresh: refreshTicketCharts,
};

window.TicketPlatformStadiumEditor = {
    init: initStadiumEditors,
};
